/**
 * Admin analytics response shapes — backed by the `{env}-optr-analytics` stack
 * (three org-scoped, admin-only GET endpoints). See the frontend handoff in
 * `optr-analytics/docs/frontend-handoff.md` for the authoritative contract.
 *
 * The dotplot and summary endpoints compute asynchronously from CloudWatch
 * Logs (10–40 s cold), so both share a `pending` (202) / `ready` (200)
 * polling envelope. Days are UTC boundaries throughout.
 */

/** 202 body returned while a report is still computing. */
export interface AnalyticsPending {
  status: 'pending';
  startedAt?: string;
  retryAfterMs?: number;
  /** Previous compute failed; the retry is already in flight. Show subtly. */
  lastError?: string;
}

export interface AnalyticsMeta {
  computedAt: string;
  /** A fresh compute is running behind the served (cached) data. */
  stale: boolean;
  source: 'logs-insights' | 'athena' | string;
  days: number;
  logGroupCount: number;
  // dotplot-only fields
  windowStart?: string;
  windowEnd?: string;
  totalUsers?: number;
  returnedUsers?: number;
  truncated?: boolean;
}

/** A single user×day cell. Absent cells mean no activity that day. */
export interface DotplotCell {
  /** Human-engagement events that day (first_seen + automation excluded). */
  total: number;
  /** Onboarding day → draw the ring. */
  firstSeen?: boolean;
  /**
   * Automation-generated message volume that day (event/schedule digests,
   * delegation results, background agent turns). Kept out of `total` /
   * `byCategory`; per-event detail lives under `byEvent[AUTOMATION_EVENT]`.
   */
  automation?: number;
  byCategory: Record<string, number>;
  byEvent: Record<string, number>;
}

export interface DotplotUser {
  /** Always present; enrichment fields may be absent (deleted users etc.). */
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  firstActiveDay?: string;
  /** Keyed by `YYYY-MM-DD` (UTC). */
  cells: Record<string, DotplotCell>;
}

export interface DotplotReady {
  status: 'ready';
  meta: AnalyticsMeta;
  /** UTC days, ascending. */
  days: string[];
  /** Pre-sorted by cohort (firstActiveDay asc, then userId) and pre-enriched. */
  users: DotplotUser[];
}

export type DotplotResponse = DotplotReady | AnalyticsPending;

export interface SummaryDailyPoint {
  day: string;
  activeUsers: number;
  events: number;
}

export interface SummaryEventCount {
  event: string;
  category: string;
  count: number;
}

export interface SummaryCategoryCount {
  category: string;
  label: string;
  count: number;
}

export interface SummaryRunCount {
  triggerSource: 'user' | 'event' | 'scheduler' | 'agent' | string;
  agentRuntime: 'agentcore' | 'claude' | string;
  count: number;
}

export interface SummaryToolGroupCount {
  toolGroup: string;
  count: number;
}

/** Aggregate message volume split by who/what produced it. */
export interface SummaryMessageCount {
  entry: 'human' | 'automation' | 'agent';
  count: number;
}

export interface SummaryReady {
  status: 'ready';
  meta: AnalyticsMeta;
  daily: SummaryDailyPoint[];
  byEvent: SummaryEventCount[];
  byCategory: SummaryCategoryCount[];
  runs: SummaryRunCount[];
  toolGroups: SummaryToolGroupCount[];
  messages: SummaryMessageCount[];
}

export type SummaryResponse = SummaryReady | AnalyticsPending;

export interface CatalogCategory {
  key: string;
  label: string;
  events: string[];
  color: { light: string; dark: string };
}

/** Static category catalog + palette — render legend/colors from this. */
export interface CatalogResponse {
  schemaVersion: number;
  categories: CatalogCategory[];
  markers: { firstSeen: string };
}

/** Narrowing helper shared by the hooks/components. */
export function isReady<T extends { status: string }>(
  r: T | AnalyticsPending | undefined,
): r is T {
  return !!r && r.status === 'ready';
}
